import accessTokenService from '../Services/access-token.service';
import passwordService from '../Services/password.service';
import { Notification } from '../utils';
import useDocumentTitle from './document-title.hook';
import { useCallback, useEffect, useState } from 'react';

const homePage = process.env.REACT_APP_PUBLIC_HOME_PAGE || '';

interface IState {
  loading: boolean;
  type: string;
  reCaptchaToken?: string;
  result?: { message: string[]; response?: string };
}

const useForgotPassword = () => {
  useDocumentTitle('Forgot Password')
  const [state, setState] = useState<IState>({
    loading: false,
    type: '',
  });

  const updateState = (newStateOrFn: Partial<IState>) => (
    setState(prevState => ({
      ...prevState,
      ...newStateOrFn
    }))
  );


  const onCaptchaVerify = useCallback((token: string | null) => {
    if (token) {
      updateState({ reCaptchaToken: token });
      return;
    }

    updateState({ reCaptchaToken: undefined });
  }, []);

  const requestResetPassword = (values: IRequestResetPassword) => {
    const DEFAULT_ERROR_MESSAGE = 'You failed the reCAPTCHA verification test. Please try again later.'

    if (!state.reCaptchaToken) {
      Notification.error({ description: DEFAULT_ERROR_MESSAGE });
      return;
    }

    updateState({ loading: true })
    passwordService.requestResetPassword(values, state.reCaptchaToken)
      .then((res) => {
        if (res.type === 'error') {
          Notification.error({ description: DEFAULT_ERROR_MESSAGE });
          return;
        }

        Notification.success({ description: 'An email with a password reset link has been sent. Please check your inbox!' });
      })
      .catch(error => {
        Notification.error({ description: DEFAULT_ERROR_MESSAGE });
        console.error(error);
      })
      .finally(() => (
        updateState({ loading: false })
      ));
  };

  const resetPasswordByToken = async (values: IResetPasswordByToken) => {
    updateState({ loading: true })
    await passwordService.resetPasswordByToken(values).then((data) => {
      updateState({ loading: false })
      if (data.type === 'success') {
        const loginRoute = `/login?msg=Your password has been reset successfully.&error=false`;
        window.location.href = loginRoute;
      }
    });
  };

  useEffect(() => {
    const accessToken = accessTokenService.get();

    if (accessToken) {
      window.location.href = homePage;
    }
  }, []);

  return { requestResetPassword, resetPasswordByToken, onCaptchaVerify, state };
};

export default useForgotPassword;
